/**
 * ISO 3166-1 alpha-2 ↔ alpha-3 conversion maps and country name list.
 * Salesforce uses alpha-2; Smarty International API uses alpha-3.
 *
 * Reference: https://www.iso.org/iso-3166-country-codes.html
 * Full code list: https://www.iban.com/country-codes
 */

/** Maps alpha-2 → { alpha3, name } */
const COUNTRIES: Record<string, { alpha3: string; name: string }> = {
  AF: { alpha3: 'AFG', name: 'Afghanistan' },
  AX: { alpha3: 'ALA', name: 'Åland Islands' },
  AL: { alpha3: 'ALB', name: 'Albania' },
  DZ: { alpha3: 'DZA', name: 'Algeria' },
  AS: { alpha3: 'ASM', name: 'American Samoa' },
  AD: { alpha3: 'AND', name: 'Andorra' },
  AO: { alpha3: 'AGO', name: 'Angola' },
  AI: { alpha3: 'AIA', name: 'Anguilla' },
  AQ: { alpha3: 'ATA', name: 'Antarctica' },
  AG: { alpha3: 'ATG', name: 'Antigua and Barbuda' },
  AR: { alpha3: 'ARG', name: 'Argentina' },
  AM: { alpha3: 'ARM', name: 'Armenia' },
  AW: { alpha3: 'ABW', name: 'Aruba' },
  AU: { alpha3: 'AUS', name: 'Australia' },
  AT: { alpha3: 'AUT', name: 'Austria' },
  AZ: { alpha3: 'AZE', name: 'Azerbaijan' },
  BS: { alpha3: 'BHS', name: 'Bahamas' },
  BH: { alpha3: 'BHR', name: 'Bahrain' },
  BD: { alpha3: 'BGD', name: 'Bangladesh' },
  BB: { alpha3: 'BRB', name: 'Barbados' },
  BY: { alpha3: 'BLR', name: 'Belarus' },
  BE: { alpha3: 'BEL', name: 'Belgium' },
  BZ: { alpha3: 'BLZ', name: 'Belize' },
  BJ: { alpha3: 'BEN', name: 'Benin' },
  BM: { alpha3: 'BMU', name: 'Bermuda' },
  BT: { alpha3: 'BTN', name: 'Bhutan' },
  BO: { alpha3: 'BOL', name: 'Bolivia' },
  BQ: { alpha3: 'BES', name: 'Bonaire, Sint Eustatius and Saba' },
  BA: { alpha3: 'BIH', name: 'Bosnia and Herzegovina' },
  BW: { alpha3: 'BWA', name: 'Botswana' },
  BV: { alpha3: 'BVT', name: 'Bouvet Island' },
  BR: { alpha3: 'BRA', name: 'Brazil' },
  IO: { alpha3: 'IOT', name: 'British Indian Ocean Territory' },
  BN: { alpha3: 'BRN', name: 'Brunei Darussalam' },
  BG: { alpha3: 'BGR', name: 'Bulgaria' },
  BF: { alpha3: 'BFA', name: 'Burkina Faso' },
  BI: { alpha3: 'BDI', name: 'Burundi' },
  CV: { alpha3: 'CPV', name: 'Cabo Verde' },
  KH: { alpha3: 'KHM', name: 'Cambodia' },
  CM: { alpha3: 'CMR', name: 'Cameroon' },
  CA: { alpha3: 'CAN', name: 'Canada' },
  KY: { alpha3: 'CYM', name: 'Cayman Islands' },
  CF: { alpha3: 'CAF', name: 'Central African Republic' },
  TD: { alpha3: 'TCD', name: 'Chad' },
  CL: { alpha3: 'CHL', name: 'Chile' },
  CN: { alpha3: 'CHN', name: 'China' },
  CX: { alpha3: 'CXR', name: 'Christmas Island' },
  CC: { alpha3: 'CCK', name: 'Cocos (Keeling) Islands' },
  CO: { alpha3: 'COL', name: 'Colombia' },
  KM: { alpha3: 'COM', name: 'Comoros' },
  CG: { alpha3: 'COG', name: 'Congo' },
  CD: { alpha3: 'COD', name: 'Congo, Democratic Republic of the' },
  CK: { alpha3: 'COK', name: 'Cook Islands' },
  CR: { alpha3: 'CRI', name: 'Costa Rica' },
  CI: { alpha3: 'CIV', name: "Côte d'Ivoire" },
  HR: { alpha3: 'HRV', name: 'Croatia' },
  CU: { alpha3: 'CUB', name: 'Cuba' },
  CW: { alpha3: 'CUW', name: 'Curaçao' },
  CY: { alpha3: 'CYP', name: 'Cyprus' },
  CZ: { alpha3: 'CZE', name: 'Czechia' },
  DK: { alpha3: 'DNK', name: 'Denmark' },
  DJ: { alpha3: 'DJI', name: 'Djibouti' },
  DM: { alpha3: 'DMA', name: 'Dominica' },
  DO: { alpha3: 'DOM', name: 'Dominican Republic' },
  EC: { alpha3: 'ECU', name: 'Ecuador' },
  EG: { alpha3: 'EGY', name: 'Egypt' },
  SV: { alpha3: 'SLV', name: 'El Salvador' },
  GQ: { alpha3: 'GNQ', name: 'Equatorial Guinea' },
  ER: { alpha3: 'ERI', name: 'Eritrea' },
  EE: { alpha3: 'EST', name: 'Estonia' },
  SZ: { alpha3: 'SWZ', name: 'Eswatini' },
  ET: { alpha3: 'ETH', name: 'Ethiopia' },
  FK: { alpha3: 'FLK', name: 'Falkland Islands (Malvinas)' },
  FO: { alpha3: 'FRO', name: 'Faroe Islands' },
  FJ: { alpha3: 'FJI', name: 'Fiji' },
  FI: { alpha3: 'FIN', name: 'Finland' },
  FR: { alpha3: 'FRA', name: 'France' },
  GF: { alpha3: 'GUF', name: 'French Guiana' },
  PF: { alpha3: 'PYF', name: 'French Polynesia' },
  TF: { alpha3: 'ATF', name: 'French Southern Territories' },
  GA: { alpha3: 'GAB', name: 'Gabon' },
  GM: { alpha3: 'GMB', name: 'Gambia' },
  GE: { alpha3: 'GEO', name: 'Georgia' },
  DE: { alpha3: 'DEU', name: 'Germany' },
  GH: { alpha3: 'GHA', name: 'Ghana' },
  GI: { alpha3: 'GIB', name: 'Gibraltar' },
  GR: { alpha3: 'GRC', name: 'Greece' },
  GL: { alpha3: 'GRL', name: 'Greenland' },
  GD: { alpha3: 'GRD', name: 'Grenada' },
  GP: { alpha3: 'GLP', name: 'Guadeloupe' },
  GU: { alpha3: 'GUM', name: 'Guam' },
  GT: { alpha3: 'GTM', name: 'Guatemala' },
  GG: { alpha3: 'GGY', name: 'Guernsey' },
  GN: { alpha3: 'GIN', name: 'Guinea' },
  GW: { alpha3: 'GNB', name: 'Guinea-Bissau' },
  GY: { alpha3: 'GUY', name: 'Guyana' },
  HT: { alpha3: 'HTI', name: 'Haiti' },
  HM: { alpha3: 'HMD', name: 'Heard Island and McDonald Islands' },
  VA: { alpha3: 'VAT', name: 'Holy See' },
  HN: { alpha3: 'HND', name: 'Honduras' },
  HK: { alpha3: 'HKG', name: 'Hong Kong' },
  HU: { alpha3: 'HUN', name: 'Hungary' },
  IS: { alpha3: 'ISL', name: 'Iceland' },
  IN: { alpha3: 'IND', name: 'India' },
  ID: { alpha3: 'IDN', name: 'Indonesia' },
  IR: { alpha3: 'IRN', name: 'Iran' },
  IQ: { alpha3: 'IRQ', name: 'Iraq' },
  IE: { alpha3: 'IRL', name: 'Ireland' },
  IM: { alpha3: 'IMN', name: 'Isle of Man' },
  IL: { alpha3: 'ISR', name: 'Israel' },
  IT: { alpha3: 'ITA', name: 'Italy' },
  JM: { alpha3: 'JAM', name: 'Jamaica' },
  JP: { alpha3: 'JPN', name: 'Japan' },
  JE: { alpha3: 'JEY', name: 'Jersey' },
  JO: { alpha3: 'JOR', name: 'Jordan' },
  KZ: { alpha3: 'KAZ', name: 'Kazakhstan' },
  KE: { alpha3: 'KEN', name: 'Kenya' },
  KI: { alpha3: 'KIR', name: 'Kiribati' },
  KP: { alpha3: 'PRK', name: "Korea, Democratic People's Republic of" },
  KR: { alpha3: 'KOR', name: 'Korea, Republic of' },
  KW: { alpha3: 'KWT', name: 'Kuwait' },
  KG: { alpha3: 'KGZ', name: 'Kyrgyzstan' },
  LA: { alpha3: 'LAO', name: "Lao People's Democratic Republic" },
  LV: { alpha3: 'LVA', name: 'Latvia' },
  LB: { alpha3: 'LBN', name: 'Lebanon' },
  LS: { alpha3: 'LSO', name: 'Lesotho' },
  LR: { alpha3: 'LBR', name: 'Liberia' },
  LY: { alpha3: 'LBY', name: 'Libya' },
  LI: { alpha3: 'LIE', name: 'Liechtenstein' },
  LT: { alpha3: 'LTU', name: 'Lithuania' },
  LU: { alpha3: 'LUX', name: 'Luxembourg' },
  MO: { alpha3: 'MAC', name: 'Macao' },
  MG: { alpha3: 'MDG', name: 'Madagascar' },
  MW: { alpha3: 'MWI', name: 'Malawi' },
  MY: { alpha3: 'MYS', name: 'Malaysia' },
  MV: { alpha3: 'MDV', name: 'Maldives' },
  ML: { alpha3: 'MLI', name: 'Mali' },
  MT: { alpha3: 'MLT', name: 'Malta' },
  MH: { alpha3: 'MHL', name: 'Marshall Islands' },
  MQ: { alpha3: 'MTQ', name: 'Martinique' },
  MR: { alpha3: 'MRT', name: 'Mauritania' },
  MU: { alpha3: 'MUS', name: 'Mauritius' },
  YT: { alpha3: 'MYT', name: 'Mayotte' },
  MX: { alpha3: 'MEX', name: 'Mexico' },
  FM: { alpha3: 'FSM', name: 'Micronesia' },
  MD: { alpha3: 'MDA', name: 'Moldova' },
  MC: { alpha3: 'MCO', name: 'Monaco' },
  MN: { alpha3: 'MNG', name: 'Mongolia' },
  ME: { alpha3: 'MNE', name: 'Montenegro' },
  MS: { alpha3: 'MSR', name: 'Montserrat' },
  MA: { alpha3: 'MAR', name: 'Morocco' },
  MZ: { alpha3: 'MOZ', name: 'Mozambique' },
  MM: { alpha3: 'MMR', name: 'Myanmar' },
  NA: { alpha3: 'NAM', name: 'Namibia' },
  NR: { alpha3: 'NRU', name: 'Nauru' },
  NP: { alpha3: 'NPL', name: 'Nepal' },
  NL: { alpha3: 'NLD', name: 'Netherlands' },
  NC: { alpha3: 'NCL', name: 'New Caledonia' },
  NZ: { alpha3: 'NZL', name: 'New Zealand' },
  NI: { alpha3: 'NIC', name: 'Nicaragua' },
  NE: { alpha3: 'NER', name: 'Niger' },
  NG: { alpha3: 'NGA', name: 'Nigeria' },
  NU: { alpha3: 'NIU', name: 'Niue' },
  NF: { alpha3: 'NFK', name: 'Norfolk Island' },
  MK: { alpha3: 'MKD', name: 'North Macedonia' },
  MP: { alpha3: 'MNP', name: 'Northern Mariana Islands' },
  NO: { alpha3: 'NOR', name: 'Norway' },
  OM: { alpha3: 'OMN', name: 'Oman' },
  PK: { alpha3: 'PAK', name: 'Pakistan' },
  PW: { alpha3: 'PLW', name: 'Palau' },
  PS: { alpha3: 'PSE', name: 'Palestine, State of' },
  PA: { alpha3: 'PAN', name: 'Panama' },
  PG: { alpha3: 'PNG', name: 'Papua New Guinea' },
  PY: { alpha3: 'PRY', name: 'Paraguay' },
  PE: { alpha3: 'PER', name: 'Peru' },
  PH: { alpha3: 'PHL', name: 'Philippines' },
  PN: { alpha3: 'PCN', name: 'Pitcairn' },
  PL: { alpha3: 'POL', name: 'Poland' },
  PT: { alpha3: 'PRT', name: 'Portugal' },
  PR: { alpha3: 'PRI', name: 'Puerto Rico' },
  QA: { alpha3: 'QAT', name: 'Qatar' },
  RE: { alpha3: 'REU', name: 'Réunion' },
  RO: { alpha3: 'ROU', name: 'Romania' },
  RU: { alpha3: 'RUS', name: 'Russian Federation' },
  RW: { alpha3: 'RWA', name: 'Rwanda' },
  BL: { alpha3: 'BLM', name: 'Saint Barthélemy' },
  SH: { alpha3: 'SHN', name: 'Saint Helena, Ascension and Tristan da Cunha' },
  KN: { alpha3: 'KNA', name: 'Saint Kitts and Nevis' },
  LC: { alpha3: 'LCA', name: 'Saint Lucia' },
  MF: { alpha3: 'MAF', name: 'Saint Martin (French part)' },
  PM: { alpha3: 'SPM', name: 'Saint Pierre and Miquelon' },
  VC: { alpha3: 'VCT', name: 'Saint Vincent and the Grenadines' },
  WS: { alpha3: 'WSM', name: 'Samoa' },
  SM: { alpha3: 'SMR', name: 'San Marino' },
  ST: { alpha3: 'STP', name: 'Sao Tome and Principe' },
  SA: { alpha3: 'SAU', name: 'Saudi Arabia' },
  SN: { alpha3: 'SEN', name: 'Senegal' },
  RS: { alpha3: 'SRB', name: 'Serbia' },
  SC: { alpha3: 'SYC', name: 'Seychelles' },
  SL: { alpha3: 'SLE', name: 'Sierra Leone' },
  SG: { alpha3: 'SGP', name: 'Singapore' },
  SX: { alpha3: 'SXM', name: 'Sint Maarten (Dutch part)' },
  SK: { alpha3: 'SVK', name: 'Slovakia' },
  SI: { alpha3: 'SVN', name: 'Slovenia' },
  SB: { alpha3: 'SLB', name: 'Solomon Islands' },
  SO: { alpha3: 'SOM', name: 'Somalia' },
  ZA: { alpha3: 'ZAF', name: 'South Africa' },
  GS: { alpha3: 'SGS', name: 'South Georgia and the South Sandwich Islands' },
  SS: { alpha3: 'SSD', name: 'South Sudan' },
  ES: { alpha3: 'ESP', name: 'Spain' },
  LK: { alpha3: 'LKA', name: 'Sri Lanka' },
  SD: { alpha3: 'SDN', name: 'Sudan' },
  SR: { alpha3: 'SUR', name: 'Suriname' },
  SJ: { alpha3: 'SJM', name: 'Svalbard and Jan Mayen' },
  SE: { alpha3: 'SWE', name: 'Sweden' },
  CH: { alpha3: 'CHE', name: 'Switzerland' },
  SY: { alpha3: 'SYR', name: 'Syrian Arab Republic' },
  TW: { alpha3: 'TWN', name: 'Taiwan' },
  TJ: { alpha3: 'TJK', name: 'Tajikistan' },
  TZ: { alpha3: 'TZA', name: 'Tanzania' },
  TH: { alpha3: 'THA', name: 'Thailand' },
  TL: { alpha3: 'TLS', name: 'Timor-Leste' },
  TG: { alpha3: 'TGO', name: 'Togo' },
  TK: { alpha3: 'TKL', name: 'Tokelau' },
  TO: { alpha3: 'TON', name: 'Tonga' },
  TT: { alpha3: 'TTO', name: 'Trinidad and Tobago' },
  TN: { alpha3: 'TUN', name: 'Tunisia' },
  TR: { alpha3: 'TUR', name: 'Turkey' },
  TM: { alpha3: 'TKM', name: 'Turkmenistan' },
  TC: { alpha3: 'TCA', name: 'Turks and Caicos Islands' },
  TV: { alpha3: 'TUV', name: 'Tuvalu' },
  UG: { alpha3: 'UGA', name: 'Uganda' },
  UA: { alpha3: 'UKR', name: 'Ukraine' },
  AE: { alpha3: 'ARE', name: 'United Arab Emirates' },
  GB: { alpha3: 'GBR', name: 'United Kingdom' },
  US: { alpha3: 'USA', name: 'United States of America' },
  UM: { alpha3: 'UMI', name: 'United States Minor Outlying Islands' },
  UY: { alpha3: 'URY', name: 'Uruguay' },
  UZ: { alpha3: 'UZB', name: 'Uzbekistan' },
  VU: { alpha3: 'VUT', name: 'Vanuatu' },
  VE: { alpha3: 'VEN', name: 'Venezuela' },
  VN: { alpha3: 'VNM', name: 'Viet Nam' },
  VG: { alpha3: 'VGB', name: 'Virgin Islands (British)' },
  VI: { alpha3: 'VIR', name: 'Virgin Islands (U.S.)' },
  WF: { alpha3: 'WLF', name: 'Wallis and Futuna' },
  EH: { alpha3: 'ESH', name: 'Western Sahara' },
  YE: { alpha3: 'YEM', name: 'Yemen' },
  ZM: { alpha3: 'ZMB', name: 'Zambia' },
  ZW: { alpha3: 'ZWE', name: 'Zimbabwe' },
  // Non-ISO codes from the legacy country list. Excluded because Smarty does not support them and
  // city/state/postal are hidden fields only populated via Smarty — users would submit an incomplete
  // address. Keep in mind for a future ask if manual entry support for unsupported countries is needed.
  // XK: { alpha3: 'XKX', name: 'Kosovo' },
  // EA: { name: 'Ceuta and Melilla' },
  // IC: { name: 'Canary Islands' },
  // AN: { name: 'Netherlands Antilles (Deprecated)' },
  // CS: { name: 'Serbia and Montenegro (Deprecated)' },
};

/** Convert ISO 3166-1 alpha-2 to alpha-3. Returns undefined if not found. */
export function toAlpha3(alpha2: string): string | undefined {
  return COUNTRIES[alpha2.toUpperCase()]?.alpha3;
}

/** Get the display name for an alpha-2 code. Returns undefined if not found. */
export function countryName(alpha2: string): string | undefined {
  return COUNTRIES[alpha2.toUpperCase()]?.name;
}

// US territories handled by the domestic Smarty API — excluded from the international dropdown.
export const DOMESTIC_CODES = new Set(['US', 'GU', 'PR', 'VI']);

/**
 * Sorted list of international countries for use in Select/ComboBox components.
 * Excludes US and its territories (GU, PR, VI) which use the US domestic address flow.
 * id = alpha-2 code (compatible with Salesforce MailingCountryCode).
 */
export const COUNTRY_OPTIONS: { id: string; label: string }[] = Object.entries(COUNTRIES)
  .filter(([alpha2]) => !DOMESTIC_CODES.has(alpha2))
  .map(([alpha2, { name }]) => ({ id: alpha2, label: name }))
  .sort((a, b) => a.label.localeCompare(b.label));
